// Subscriber data, types, mutations, and real-time streaming for WaTag (WP-03).
// Builds strictly against the backend contract in docs/agents/WP-03-backend/report.md.

import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useTenant } from './tenant';

export type OptInStatus = 'subscribed' | 'unsubscribed' | 'pending';

export interface SubscriberNote {
  id: string;
  text: string;
  authorUid: string;
  authorEmail: string | null;
  createdAt: number;
}

export interface Subscriber {
  id: string; // phone digits
  phone: string; // backwards compatibility alias for phoneE164
  phoneE164: string;
  name: string;
  email?: string | null;
  channel?: string;
  source?: string;
  country?: string | null;
  timezone?: string | null;
  language?: string | null;
  labelIds: string[];
  labels: string[]; // legacy string names
  listIds: string[];
  sequenceIds?: string[];
  customFields: Record<string, any>;
  botPaused?: boolean;
  aiPaused?: boolean;
  optIn?: {
    status: OptInStatus;
    marketingOptIn?: boolean;
    optedOutAt?: number | null;
    optOutSource?: string | null;
    consentNote?: string | null;
  };
  status: OptInStatus;
  notes?: SubscriberNote[];
  agentUid?: string | null;
  integrationIds?: string[];
  currentFlowId?: string | null;
  currentStepId?: string | null;
  createdAt?: number;
  updatedAt?: number;
  subscribedAt?: number;
  lastSeenAt?: number;
  deleted?: boolean;
}

export interface CustomFieldFilterRule {
  op: 'eq' | 'neq' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'exists' | 'not_exists';
  value?: any;
}

export interface SubscriberFilter {
  search?: string;
  status?: OptInStatus | OptInStatus[];
  labelIds?: string[];
  listIds?: string[];
  integrationIds?: string[];
  country?: string | string[];
  language?: string | string[];
  agentUid?: string | null;
  botPaused?: boolean;
  aiPaused?: boolean;
  hasNotes?: boolean;
  createdAfter?: number;
  createdBefore?: number;
  lastSeenAfter?: number;
  lastSeenBefore?: number;
  customFields?: Record<string, CustomFieldFilterRule | any>;
}

function call<TReq, TRes>(name: string, opts?: { coerceNulls?: boolean }) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => {
    const payload = opts?.coerceNulls === false ? data : stripUndefined(data);
    return (await fn(payload)).data;
  };
}

// ── Callables ─────────────────────────────────────────────────────────────────

export const subscriberCreate = call<
  {
    phoneE164: string;
    name?: string;
    email?: string;
    language?: string;
    country?: string;
    timezone?: string;
    labelIds?: string[];
    listIds?: string[];
    customFields?: Record<string, any>;
    botPaused?: boolean;
    aiPaused?: boolean;
    /** How/when the contact agreed to marketing (10+ chars). Turns on marketing consent. */
    marketingConsentNote?: string;
  },
  { ok: true; subscriber: Record<string, any> }
>('subscriberCreate');

export type BulkAction =
  | 'addLabel'
  | 'removeLabel'
  | 'addList'
  | 'removeList'
  | 'assignSequence'
  | 'delete'
  | 'unsubscribe'
  | 'resubscribe'
  | 'recordMarketingConsent'
  | 'resetInputFlow'
  | 'clearHistory';

export const subscriberBulk = call<
  {
    ids?: string[];
    filter?: SubscriberFilter;
    action: BulkAction;
    labelId?: string;
    listId?: string;
    sequenceId?: string;
    consentNote?: string;
  },
  { ok: true; affectedCount: number; skippedOptedOut?: number }
>('subscriberBulk');

export const subscriberExport = call<
  { filter?: SubscriberFilter; format?: 'csv' | 'xlsx' },
  { ok: true; exportId: string; downloadUrl: string; storagePath: string; totalRows: number }
>('subscriberExport');

export const subscriberImportTemplate = call<
  Record<string, never>,
  { ok: true; csv: string; headers: string[] }
>('subscriberImportTemplate');

export const subscriberSetFlags = call<
  {
    subscriberId: string;
    botPaused?: boolean;
    aiPaused?: boolean;
    status?: OptInStatus;
  },
  { ok: true }
>('subscriberSetFlags');

export const subscriberNoteAdd = call<
  { subscriberId: string; text: string },
  { ok: true; note: SubscriberNote }
>('subscriberNoteAdd');

export const subscriberNoteDelete = call<
  { subscriberId: string; noteId: string },
  { ok: true }
>('subscriberNoteDelete');

/**
 * Assigns or unassigns an agent.
 * Setting `agentUid: null` unassigns the subscriber.
 * Uses coerceNulls: false so `null` is preserved across the wire.
 */
export const subscriberAssignAgent = call<
  { subscriberId: string; agentUid: string | null },
  { ok: true; agentUid: string | null }
>('subscriberAssignAgent', { coerceNulls: false });

export const updateSubscriber = call<
  {
    subscriberId: string;
    name?: string;
    email?: string;
    language?: string;
    country?: string;
    timezone?: string;
    labels?: string[];
    labelIds?: string[];
    listIds?: string[];
    customFields?: Record<string, any>;
    botPaused?: boolean;
    aiPaused?: boolean;
  },
  { ok: true }
>('updateSubscriber');

export const importSubscribers = call<
  { rows: Array<Record<string, any>>; marketingConsentNote?: string },
  { ok: true; imported: number; skipped: number; duplicates: number; consentGranted?: number }
>('importSubscribers');

export const importGoogleSheet = call<
  { sheetUrl: string },
  { ok: true; imported: number; skipped: number; duplicates: number }
>('importGoogleSheet');

export const importGoogleContacts = call<
  Record<string, never>,
  { ok: true }
>('importGoogleContacts');

export const deleteSubscriber = call<{ subscriberId: string }, { ok: boolean }>('deleteSubscriber');
export const bulkLabelSubscribers = call<{ ids: string[]; label: string }, { ok: boolean; count?: number }>('bulkLabelSubscribers');
export const bulkAddToSequence = call<{ ids: string[]; sequenceId: string }, { ok: boolean; count?: number }>('bulkAddToSequence');

// ── Pure Client Filter Engine ────────────────────────────────────────────────

export function matchesSubscriberFilter(sub: Subscriber, filter?: SubscriberFilter): boolean {
  if (!filter || Object.keys(filter).length === 0) {
    return true;
  }

  // 1. Search (phone, name, email, id)
  if (filter.search) {
    const q = filter.search.toLowerCase().trim();
    const phone = String(sub.phoneE164 ?? '').toLowerCase();
    const name = String(sub.name ?? '').toLowerCase();
    const email = String(sub.email ?? '').toLowerCase();
    const id = String(sub.id ?? '').toLowerCase();
    if (!phone.includes(q) && !name.includes(q) && !email.includes(q) && !id.includes(q)) {
      return false;
    }
  }

  // 2. Status
  if (filter.status !== undefined) {
    const currentStatus = sub.optIn?.status ?? sub.status ?? 'pending';
    const allowed = Array.isArray(filter.status) ? filter.status : [filter.status];
    if (!allowed.includes(currentStatus)) return false;
  }

  // 3. Label IDs
  if (filter.labelIds && filter.labelIds.length > 0) {
    const subLabels: string[] = Array.isArray(sub.labelIds) ? sub.labelIds : [];
    const hasAny = filter.labelIds.some((lid) => subLabels.includes(lid));
    if (!hasAny) return false;
  }

  // 4. List IDs
  if (filter.listIds && filter.listIds.length > 0) {
    const subLists: string[] = Array.isArray(sub.listIds) ? sub.listIds : [];
    const hasAny = filter.listIds.some((lid) => subLists.includes(lid));
    if (!hasAny) return false;
  }

  // 5. Integration IDs (Account filter)
  if (filter.integrationIds && filter.integrationIds.length > 0) {
    const subInts: string[] = Array.isArray(sub.integrationIds) ? sub.integrationIds : [];
    const hasAny = filter.integrationIds.some((iid) => subInts.includes(iid));
    if (!hasAny) return false;
  }

  // 6. Country
  if (filter.country !== undefined) {
    const subCountry = String(sub.country ?? '').toUpperCase();
    const allowed = Array.isArray(filter.country)
      ? filter.country.map((c) => c.toUpperCase())
      : [filter.country.toUpperCase()];
    if (!allowed.includes(subCountry)) return false;
  }

  // 7. Language
  if (filter.language !== undefined) {
    const subLang = String(sub.language ?? '').toLowerCase();
    const allowed = Array.isArray(filter.language)
      ? filter.language.map((l) => l.toLowerCase())
      : [filter.language.toLowerCase()];
    if (!allowed.includes(subLang)) return false;
  }

  // 8. Custom Fields
  if (filter.customFields && Object.keys(filter.customFields).length > 0) {
    const subFields = sub.customFields ?? {};
    for (const [key, rule] of Object.entries(filter.customFields)) {
      const val = subFields[key];
      if (typeof rule === 'object' && rule !== null && 'op' in rule) {
        const opRule = rule as CustomFieldFilterRule;
        switch (opRule.op) {
          case 'exists':
            if (val === undefined || val === null || val === '') return false;
            break;
          case 'not_exists':
            if (val !== undefined && val !== null && val !== '') return false;
            break;
          case 'eq':
            if (String(val) !== String(opRule.value)) return false;
            break;
          case 'neq':
            if (String(val) === String(opRule.value)) return false;
            break;
          case 'contains':
            if (!String(val ?? '').toLowerCase().includes(String(opRule.value ?? '').toLowerCase()))
              return false;
            break;
          case 'gt':
            if (!(Number(val) > Number(opRule.value))) return false;
            break;
          case 'gte':
            if (!(Number(val) >= Number(opRule.value))) return false;
            break;
          case 'lt':
            if (!(Number(val) < Number(opRule.value))) return false;
            break;
          case 'lte':
            if (!(Number(val) <= Number(opRule.value))) return false;
            break;
        }
      } else {
        if (String(val) !== String(rule)) return false;
      }
    }
  }

  return true;
}

// ── Real-Time Subscribers Stream ─────────────────────────────────────────────

export interface SubscribersState {
  subscribers: Subscriber[];
  labels: string[];
  loading: boolean;
  error: string | null;
}

export function useSubscribers(): SubscribersState {
  const { tenantId } = useTenant();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setSubscribers([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, `tenants/${tenantId}/subscribers`),
      orderBy('createdAt', 'desc'),
      limit(2000),
    );

    return onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => {
            const data = d.data() as any;
            if (data.deleted) return null; // skip tombstone docs
            const phoneE164 = data.phoneE164 || `+${d.id}`;
            return {
              id: d.id,
              phone: phoneE164,
              phoneE164,
              name: data.name || '',
              email: data.email ?? null,
              channel: data.channel || 'whatsapp',
              source: data.source || 'manual',
              country: data.country ?? null,
              timezone: data.timezone ?? null,
              language: data.language ?? null,
              labelIds: Array.isArray(data.labelIds) ? data.labelIds : [],
              labels: Array.isArray(data.labels) ? data.labels : [],
              listIds: Array.isArray(data.listIds) ? data.listIds : [],
              sequenceIds: Array.isArray(data.sequenceIds) ? data.sequenceIds : [],
              customFields: data.customFields || {},
              botPaused: data.botPaused ?? false,
              aiPaused: data.aiPaused ?? false,
              optIn: data.optIn,
              status: (data.optIn?.status || data.status || 'pending') as OptInStatus,
              notes: Array.isArray(data.notes) ? data.notes : [],
              agentUid: data.agentUid ?? null,
              integrationIds: Array.isArray(data.integrationIds) ? data.integrationIds : [],
              currentFlowId: data.currentFlowId ?? null,
              currentStepId: data.currentStepId ?? null,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              subscribedAt: data.subscribedAt,
              lastSeenAt: data.lastSeenAt,
            } as Subscriber;
          })
          .filter(Boolean) as Subscriber[];

        setSubscribers(items);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [tenantId]);

  const labels = useMemo(() => {
    const set = new Set<string>();
    for (const s of subscribers) {
      if (Array.isArray(s.labels)) {
        for (const l of s.labels) if (l) set.add(l);
      }
    }
    return Array.from(set);
  }, [subscribers]);

  return { subscribers, labels, loading, error };
}

// ── Statistics Helper ────────────────────────────────────────────────────────

export function calculateSubscriberStats(subscribers: Subscriber[]) {
  const total = subscribers.length;
  if (total === 0) {
    return { total: 0, subscribedPct: 0, inSequencesPct: 0 };
  }

  const subscribedCount = subscribers.filter((s) => s.status === 'subscribed').length;
  const inSequencesCount = subscribers.filter(
    (s) => s.sequenceIds && s.sequenceIds.length > 0,
  ).length;

  const subscribedPct = Math.round((subscribedCount / total) * 100);
  const inSequencesPct = Math.round((inSequencesCount / total) * 100);

  return {
    total,
    subscribedPct,
    inSequencesPct,
  };
}
